/**
 * Single source of truth for SEO surface area.
 *
 * Everything that describes which URLs may be indexed lives here:
 *  - the sitemap index and its child sitemaps
 *  - the /robots.txt server route (src/routes/robots[.]txt.ts)
 *  - the X-Robots-Tag request middleware in src/start.ts
 *  - the SEO regression tests in tests/seo-regression.test.ts
 */

export const BASE_URL = "https://easements.com";

export interface SitemapEntry {
  path: string;
  changefreq?: "always" | "hourly" | "daily" | "weekly" | "monthly" | "yearly" | "never";
  priority?: string;
}

/** Public, indexable content pages. */
export const PAGE_ENTRIES: SitemapEntry[] = [
  { path: "/", changefreq: "weekly", priority: "1.0" },
  { path: "/resources", changefreq: "weekly", priority: "0.8" },
  { path: "/public-records", changefreq: "weekly", priority: "0.8" },
  { path: "/questions", changefreq: "weekly", priority: "0.8" },
  { path: "/guides", changefreq: "weekly", priority: "0.9" },
  { path: "/articles", changefreq: "weekly", priority: "0.9" },
  { path: "/title-analyzer", changefreq: "weekly", priority: "0.8" },
  { path: "/easement-value-calculator", changefreq: "monthly", priority: "0.8" },
  { path: "/easement-law", changefreq: "monthly", priority: "0.8" },
  { path: "/state-laws", changefreq: "monthly", priority: "0.8" },
  { path: "/county-lookup", changefreq: "weekly", priority: "0.9" },
  { path: "/easement-faq", changefreq: "monthly", priority: "0.8" },
  { path: "/easement-types", changefreq: "monthly", priority: "0.9" },
  { path: "/easement-width-checker", changefreq: "monthly", priority: "0.9" },
  { path: "/easement-lookup", changefreq: "weekly", priority: "0.9" },
  { path: "/parcel-lookup", changefreq: "weekly", priority: "0.9" },
  { path: "/editorial-standards", changefreq: "yearly", priority: "0.4" },
  { path: "/editorial/joshua-cole", changefreq: "yearly", priority: "0.4" },
  { path: "/developers", changefreq: "monthly", priority: "0.4" },
  { path: "/local-search", changefreq: "weekly", priority: "0.8" },
  { path: "/easement-questions-landowners", changefreq: "monthly", priority: "0.8" },
  { path: "/find-my-easement-document", changefreq: "monthly", priority: "0.8" },
  { path: "/how-to-read-an-easement-document", changefreq: "monthly", priority: "0.8" },
  { path: "/deed-faq", changefreq: "monthly", priority: "0.8" },
  { path: "/audit", changefreq: "weekly", priority: "0.9" },
  { path: "/deed-map", changefreq: "weekly", priority: "0.9" },
  { path: "/easement-terms-explained", changefreq: "monthly", priority: "0.7" },

  { path: "/easement-guides", changefreq: "weekly", priority: "0.8" },
  { path: "/county-offices", changefreq: "monthly", priority: "0.8" },
  { path: "/county-recording-fees", changefreq: "monthly", priority: "0.8" },
  { path: "/county-data-sources", changefreq: "monthly", priority: "0.8" },

  { path: "/glossary", changefreq: "monthly", priority: "0.7" },
  { path: "/coverage", changefreq: "monthly", priority: "0.6" },
  { path: "/about", changefreq: "yearly", priority: "0.5" },
  { path: "/samples", changefreq: "monthly", priority: "0.7" },
  { path: "/pricing", changefreq: "monthly", priority: "0.7" },

];

/** Policy / legal pages. */
export const LEGAL_ENTRIES: SitemapEntry[] = [
  { path: "/disclaimer", changefreq: "yearly", priority: "0.3" },
  { path: "/privacy", changefreq: "yearly", priority: "0.3" },
  { path: "/terms", changefreq: "yearly", priority: "0.3" },
  { path: "/cookie-notice", changefreq: "yearly", priority: "0.3" },
];

export const CHILD_SITEMAPS = [
  "/sitemap-pages.xml",
  "/sitemap-legal.xml",
  "/sitemap-easement-law.xml",
  "/sitemap-county-guides.xml",
  "/sitemap-glossary.xml",
  "/sitemap-public-records.xml",
  "/sitemap-questions.xml",
  "/sitemap-easement-types.xml",
  "/sitemap-blog.xml",
] as const;

export const ALL_INDEXABLE_PATHS: string[] = [...PAGE_ENTRIES, ...LEGAL_ENTRIES].map((e) => e.path);

/**
 * Self-canonical, crawlable app pages that are deliberately kept OUT of the
 * sitemap: they are auth-gated or utility screens with little unique content,
 * so advertising them only spends crawl budget and feeds Google's
 * "Crawled - currently not indexed" bucket. They still carry a canonical tag
 * because legacy 301s (/login, /my-reports, /settings) point at them.
 */
export const UNLISTED_CANONICAL_PATHS: string[] = [
  "/reports",
  "/settings/branding",
  "/auth",
  // Utility screens: real pages with self-canonicals, but too thin to be worth
  // advertising in the sitemap.
  "/help",
  "/contact/support",
  "/contact/legal",
  // Sample and sign-up screens: useful to visitors, but not reading content.
  "/resources/listing-example",
  "/resources/apply",
  "/cobrand-sample",
];

/** Every path that self-references a canonical URL, sitemapped or not. */
export const ALL_CANONICAL_PATHS: string[] = [
  ...ALL_INDEXABLE_PATHS,
  ...UNLISTED_CANONICAL_PATHS,
];

/**
 * Path prefixes that must never be indexed: tokenized share links,
 * per-user report views, admin consoles and internal endpoints.
 */
export const NOINDEX_PREFIXES = [
  "/r/",
  "/e/",
  "/instant/",
  "/contact-submissions",
  "/instant-events",
  "/settings/domain",
  "/api/",
  "/lovable/",
] as const;

/** True when the pathname must be served with noindex, nofollow. */
export function isNoindexPath(pathname: string): boolean {
  return NOINDEX_PREFIXES.some(
    (prefix) =>
      pathname === prefix ||
      pathname.startsWith(prefix) ||
      (!prefix.endsWith("/") && pathname === prefix),
  );
}

export const X_ROBOTS_TAG = "noindex, nofollow";

const FRIENDLY_CRAWLERS = ["Googlebot", "Bingbot", "Twitterbot", "facebookexternalhit"];

/** Deterministic robots.txt content derived from the lists above. */
export function buildRobotsTxt(): string {
  const lines: string[] = [];
  for (const agent of FRIENDLY_CRAWLERS) {
    lines.push(`User-agent: ${agent}`, "Allow: /", "");
  }
  lines.push("User-agent: *", "Allow: /");
  lines.push("# Private, tokenized and internal routes (also sent as X-Robots-Tag)");
  for (const prefix of NOINDEX_PREFIXES) {
    lines.push(`Disallow: ${prefix}`);
  }
  lines.push("", `Sitemap: ${BASE_URL}/sitemap.xml`, "");
  return lines.join("\n");
}

/** Preview / staging hosts should never be crawled or indexed. */
export function isPreviewHost(host: string): boolean {
  const h = host.toLowerCase();
  return (
    h.includes("id-preview--") ||
    h.endsWith("-dev.lovable.app") ||
    h === "localhost" ||
    h.startsWith("localhost:") ||
    h.endsWith(".lovable.cafe")
  );
}

/** Minimal robots.txt body served on preview and staging hosts. */
export const PREVIEW_ROBOTS = `User-agent: *\nDisallow: /\n`;

/** Human label used for breadcrumb JSON-LD. */
export function breadcrumbLabel(segment: string): string {
  return segment
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * BreadcrumbList JSON-LD for a multi-level path such as /contact/support.
 * Returns null for single-level paths, which do not need breadcrumbs.
 */
export function breadcrumbJsonLd(
  path: string,
  overrides: Record<string, string> = {},
): string | null {
  const segments = path.split("/").filter(Boolean);
  if (segments.length < 2) return null;

  const items = [{ name: "Home", item: `${BASE_URL}/` }];
  let acc = "";
  for (const segment of segments) {
    acc += `/${segment}`;
    items.push({ name: overrides[acc] ?? breadcrumbLabel(segment), item: `${BASE_URL}${acc}` });
  }

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((entry, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: entry.name,
      item: entry.item,
    })),
  });
}

export function renderUrlset(entries: SitemapEntry[]): Response {
  const urls = entries.map((e) =>
    [
      `  <url>`,
      `    <loc>${BASE_URL}${e.path}</loc>`,
      e.changefreq ? `    <changefreq>${e.changefreq}</changefreq>` : null,
      e.priority ? `    <priority>${e.priority}</priority>` : null,
      `  </url>`,
    ]
      .filter(Boolean)
      .join("\n"),
  );

  const xml = [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">`,
    ...urls,
    `</urlset>`,
  ].join("\n");

  return new Response(xml, {
    headers: { "Content-Type": "application/xml", "Cache-Control": "public, max-age=3600" },
  });
}

export function renderSitemapIndex(): Response {
  const xml = [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">`,
    ...CHILD_SITEMAPS.map((path) =>
      [`  <sitemap>`, `    <loc>${BASE_URL}${path}</loc>`, `  </sitemap>`].join("\n"),
    ),
    `</sitemapindex>`,
  ].join("\n");

  return new Response(xml, {
    headers: { "Content-Type": "application/xml", "Cache-Control": "public, max-age=3600" },
  });
}
